import { Client } from '@botpress/client'
import { auth } from '../auth/index.js'

interface WorkspaceInfo {
  id: string
  name: string
  fetchedAt: number
}

class WorkspaceCache {
  private cache: Map<string, WorkspaceInfo> = new Map()
  private readonly CACHE_TTL = 60 * 60 * 1000 // 1 hour

  async getWorkspaceName(workspaceId: string): Promise<string | undefined> {
    // Check cache first
    const cached = this.cache.get(workspaceId)
    if (cached && Date.now() - cached.fetchedAt < this.CACHE_TTL) {
      return cached.name
    }

    try {
      // Get credentials to create client
      const credentials = await auth.getActiveCredentials()
      const client = new Client({
        apiUrl: credentials.apiUrl,
        token: credentials.token,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })

      // Fetch all workspaces and find the one with matching ID
      const { workspaces } = await client.listWorkspaces({})
      const workspace = workspaces.find((ws) => ws.id === workspaceId)

      if (!workspace) {
        return undefined
      }

      // Cache the result
      this.cache.set(workspaceId, {
        id: workspaceId,
        name: workspace.name,
        fetchedAt: Date.now(),
      })

      return workspace.name
    } catch (error) {
      console.error(`Failed to fetch workspace name for ${workspaceId}:`, error)
      return undefined
    }
  }

  clear(): void {
    this.cache.clear()
  }
}

// Export singleton instance
export const workspaceCache = new WorkspaceCache()
